// Configure Foreign CF DNS - the contract between the wizard shell and its
// step components.
//
// This file exists to break an import cycle, and nothing else. Every symbol
// here was previously exported from ForeignDnsRunDetail.tsx, which the five
// step files then imported FROM while the shell imported THEM - a five-file
// cycle. It was safe (hoisted `export function` declarations, nothing called at
// module scope) but it was one file away from the kind of cycle that bites at
// module-init time in a production bundle, and the fix is a pure move.
//
// Nothing in here fetches, renders or holds state that outlives a render. It is
// the shared props type, three pure readers over the run payload, and one hook.

import { useEffect, useRef } from 'react';

import type { FdDomain, FdRunDetail, FdStage, FdStageRow } from '../foreignDnsApi';

export type FdToastKind = 'success' | 'error' | 'info' | 'warn';

/** Everything a step component gets. Steps hold their own form state and
 *  nothing else; every read comes from `detail`, every write goes through
 *  fdApi and is followed by `reload()`. */
export interface FdStepProps {
  runId: string;
  detail: FdRunDetail;
  /** Re-read run + domains + stages from the server. */
  reload: () => Promise<void>;
  /** Call right after a runStage / setDomains that actually enqueued, so the
   *  shell polls through the queue-pickup gap. Do NOT call it when the response
   *  said `alreadyRunning` - nothing was started. */
  notifyEnqueued: () => void;
  /** Move the wizard pointer. Persists through setChoices, then reloads. */
  goToStep: (step: number) => Promise<void>;
  toast: (type: FdToastKind, message: string) => void;
}

/** The stage row for one stage, or null when that stage has never started.
 *  foreign_dns_run_stages only gets a row at startStage, so "no row" is the
 *  never-run state and must not be read as idle-after-a-run. */
export function findStage(stages: FdStageRow[], stage: FdStage): FdStageRow | null {
  return stages.find((s) => s.stage === stage) ?? null;
}

/** Cloudflare had no zone for this domain. Every later stage skips it, so it is
 *  excluded from every "is this stage finished" test. */
export function isZoneNotFound(d: FdDomain): boolean {
  return d.zone_id === null;
}

/**
 * Escape closes a modal, matching the convention already in this codebase
 * (MonitoringRunDetail, AdminPanels). The listener is bound only while the modal
 * is open, and `locked` suppresses it while the modal's action is in flight - a
 * close that is already running must not be abandoned half-done. The callback
 * lives in a ref so an inline arrow at the call site does not re-subscribe the
 * listener on every render.
 */
export function useEscapeToClose(open: boolean, locked: boolean, close: () => void): void {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !locked) closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, locked]);
}

/** Read a numeric field out of a stage summary blob without trusting its shape. */
export function summaryCount(row: FdStageRow | null, key: string): number | null {
  const value = row?.summary?.[key];
  return typeof value === 'number' ? value : null;
}
